class CartChecker {
  constructor() {
    /** @type {Array<any>} */
    this.cartItems = [];
    this.isLoading = false;
    /** @type {Promise<Array<any>> | null} */
    this.inflight = null;
    this.lastFetchTime = 0;
    this.cacheTimeout = 30000;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this.refreshTimer = null;
    this.setupCartEventListeners();
  }

  setupCartEventListeners() {
    const refreshCart = () => this.scheduleRefresh();
    document.addEventListener('cart:add', refreshCart);
    document.addEventListener('cart:update', refreshCart);
    document.addEventListener('cart:change', refreshCart);
  }

  scheduleRefresh() {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refreshCartData();
    }, 250);
  }

  async fetchCartData() {
    const now = Date.now();
    if (
      !this.isLoading &&
      !this.inflight &&
      now - this.lastFetchTime < this.cacheTimeout &&
      this.cartItems.length > 0
    ) {
      return this.cartItems;
    }

    if (this.inflight) {
      return this.inflight;
    }

    this.isLoading = true;
    this.inflight = (async () => {
      try {
        if (typeof window.fetchSharedCart === 'function') {
          const cartData = await window.fetchSharedCart();
          this.cartItems = cartData.items || [];
        } else {
          const response = await fetch('/cart.js');
          const cartData = await response.json();
          this.cartItems = cartData.items || [];
        }
        this.lastFetchTime = Date.now();
        return this.cartItems;
      } catch (error) {
        return this.cartItems;
      } finally {
        this.isLoading = false;
        this.inflight = null;
      }
    })();

    return this.inflight;
  }

  async isVariantInCart(variantId) {
    const cartItems = await this.fetchCartData();
    return cartItems.some(
      item => String(item.variant_id) === String(variantId)
    );
  }

  async getCartItemByVariant(variantId) {
    const cartItems = await this.fetchCartData();
    return (
      cartItems.find(item => String(item.variant_id) === String(variantId)) ||
      null
    );
  }

  async getCartVariantIds() {
    const cartItems = await this.fetchCartData();
    return cartItems.map(item => String(item.variant_id));
  }

  async refreshCartData() {
    this.lastFetchTime = 0;
    if (typeof window.invalidateSharedCart === 'function') {
      window.invalidateSharedCart();
    }
    return await this.fetchCartData();
  }

  async checkMultipleVariants(variantIds) {
    const cartVariantIds = await this.getCartVariantIds();
    const results = {};
    variantIds.forEach(variantId => {
      results[String(variantId)] = cartVariantIds.includes(String(variantId));
    });
    return results;
  }

  async getVariantQuantityInCart(variantId) {
    const cartItem = await this.getCartItemByVariant(variantId);
    return cartItem && typeof cartItem === 'object' && 'quantity' in cartItem
      ? Number(cartItem.quantity)
      : 0;
  }

  clearCache() {
    this.cartItems = [];
    this.lastFetchTime = 0;
  }
}

window.cartChecker = new CartChecker();
